import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { RootState } from '@/reducers';
import { fetchPipelines, createItem } from '@/actions/pipeline';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import {
	Command,
	CommandInput,
	CommandList,
	CommandEmpty,
	CommandGroup,
	CommandItem,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';

interface CreatePipelineItemDialogProps {
	_customer: string;
	simplePlusButton?: boolean;
	onItemCreated?: () => void;
	/** Controlled open state; leave both out and the dialog keeps its own. */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	/**
	 * false renders the dialog without its button, for a page that keeps the
	 * dialog (and what was typed) above a phone / desktop layout swap and
	 * opens it from a CreatePipelineItemButton.
	 */
	trigger?: boolean;
	/**
	 * With trigger={false}: where focus goes on close when the button that
	 * opened the dialog has gone (see DialogContent). Defaults to the
	 * CreatePipelineItemButton on screen.
	 */
	returnFocusTo?: React.ComponentProps<typeof DialogContent>['returnFocusTo'];
}

/** The contact card's "Pipeline Item" button: icon-only below 1570px. */
const PipelineItemButton = React.forwardRef<
	HTMLButtonElement,
	React.ComponentPropsWithoutRef<typeof Button>
>((props, ref) => (
	<Button
		ref={ref}
		variant="outline"
		size="sm"
		className="flex items-center justify-center min-[1570px]:space-x-2 max-[1569px]:w-9 max-[1569px]:h-9 max-[1569px]:p-0"
		aria-label="Add pipeline item"
		{...props}
	>
		<Plus size={14} />
		<span className="hidden min-[1570px]:inline">
			Pipeline Item
		</span>
	</Button>
));
PipelineItemButton.displayName = 'PipelineItemButton';

const withTooltip = (button: React.ReactNode) => (
	<TooltipProvider>
		<Tooltip>
			<TooltipTrigger asChild>{button}</TooltipTrigger>
			<TooltipContent className="min-[1570px]:hidden">
				<p>Pipeline Item</p>
			</TooltipContent>
		</Tooltip>
	</TooltipProvider>
);

/** Opens a CreatePipelineItemDialog rendered with trigger={false}. */
export const CreatePipelineItemButton: React.FC<{ onClick: () => void }> = ({
	onClick,
}) =>
	withTooltip(
		<PipelineItemButton
			aria-haspopup="dialog"
			data-opens="pipeline-item"
			onClick={onClick}
		/>
	);

export const CreatePipelineItemDialog: React.FC<
	CreatePipelineItemDialogProps
> = ({
	_customer,
	simplePlusButton = false,
	onItemCreated,
	open: openProp,
	onOpenChange,
	trigger = true,
	returnFocusTo = '[data-opens="pipeline-item"]',
}) => {
	const dispatch = useDispatch();
	const { pipelines } = useSelector((state: RootState) => state.pipeline);

	const [ownOpen, setOwnOpen] = useState(false);
	const open = openProp ?? ownOpen;
	const setOpen = onOpenChange ?? setOwnOpen;
	const [pipelineOpen, setPipelineOpen] = useState(false);
	const [columnOpen, setColumnOpen] = useState(false);
	const [selectedPipelineId, setSelectedPipelineId] = useState('');
	const [selectedColumnId, setSelectedColumnId] = useState('');
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [isCreating, setIsCreating] = useState(false);

	// Load pipelines when dialog opens
	useEffect(() => {
		if (open && pipelines.length === 0) {
			dispatch(fetchPipelines() as any);
		}
	}, [open, dispatch, pipelines.length]);

	// Auto-select first pipeline and column when available
	useEffect(() => {
		if (pipelines.length > 0 && !selectedPipelineId) {
			setSelectedPipelineId(pipelines[0]._id);
		}
	}, [pipelines, selectedPipelineId]);

	// Auto-select first column when pipeline changes
	useEffect(() => {
		const selectedPipeline = pipelines.find(
			(p) => p._id === selectedPipelineId
		);
		if (selectedPipeline && selectedPipeline.columns.length > 0) {
			setSelectedColumnId(selectedPipeline.columns[0]._id);
		}
	}, [selectedPipelineId, pipelines]);

	const selectedPipeline = pipelines.find(
		(p) => p._id === selectedPipelineId
	);
	const columns = selectedPipeline?.columns || [];

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!selectedPipelineId || !selectedColumnId || !name.trim()) {
			toast.error('Please fill in all required fields');
			return;
		}

		setIsCreating(true);

		try {
			const itemData = {
				name: name.trim(),
				description: description.trim(),
				columnId: selectedColumnId,
				_customer,
			};

			const result = await dispatch(
				createItem(selectedPipelineId, itemData) as any
			);

			if (result.success) {
				toast.success('Item created successfully!');

				// Reset form and close dialog
				setName('');
				setDescription('');
				setOpen(false);
				onItemCreated?.();
			} else {
				toast.error(result.error || 'Failed to create item');
			}
		} catch (error) {
			console.error('Error creating item:', error);
			toast.error('Failed to create item');
		} finally {
			setIsCreating(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			{!trigger ? null : simplePlusButton ? (
				<DialogTrigger asChild>
					<Button
						variant="ghost"
						size="sm"
						className="h-8 w-8 p-0 hover:bg-gray-100"
						aria-label="Add pipeline item"
					>
						<Plus className="h-4 w-4" />
					</Button>
				</DialogTrigger>
			) : (
				withTooltip(
					<DialogTrigger asChild>
						<PipelineItemButton />
					</DialogTrigger>
				)
			)}
			<DialogContent
				className="sm:max-w-md"
				// Without a DialogTrigger Radix has nowhere to hand focus back to on close.
				returnFocusTo={trigger ? undefined : returnFocusTo}
			>
				<DialogHeader>
					<DialogTitle>New Pipeline Item</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4">
					{pipelines.length > 0 && (
						<>
							<div className="space-y-2">
								<Label htmlFor="pipeline">Pipeline</Label>
								<Popover
									open={pipelineOpen}
									onOpenChange={setPipelineOpen}
								>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											role="combobox"
											aria-expanded={pipelineOpen}
											className="w-full justify-between"
										>
											{selectedPipeline?.name ||
												'Select pipeline...'}
											<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent className="w-full p-0">
										<Command>
											<CommandInput
												placeholder="Search pipelines..."
												className="h-9"
											/>
											<CommandList>
												<CommandEmpty>
													No pipeline found.
												</CommandEmpty>
												<CommandGroup>
													{pipelines.map(
														(pipeline) => (
															<CommandItem
																key={
																	pipeline._id
																}
																value={
																	pipeline._id
																}
																onSelect={() => {
																	setSelectedPipelineId(
																		pipeline._id
																	);
																	setPipelineOpen(
																		false
																	);
																}}
															>
																{pipeline.name}
																<Check
																	className={cn(
																		'ml-auto h-4 w-4',
																		selectedPipelineId ===
																			pipeline._id
																			? 'opacity-100'
																			: 'opacity-0'
																	)}
																/>
															</CommandItem>
														)
													)}
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
							</div>

							{selectedPipeline && (
								<div className="space-y-2">
									<Label htmlFor="column">Column</Label>
									<Popover
										open={columnOpen}
										onOpenChange={setColumnOpen}
									>
										<PopoverTrigger asChild>
											<Button
												variant="outline"
												role="combobox"
												aria-expanded={columnOpen}
												className="w-full justify-between"
											>
												{(() => {
													const selectedColumn =
														columns.find(
															(c) =>
																c._id ===
																selectedColumnId
														);
													return (
														<>
															<div className="flex items-center gap-2">
																{selectedColumn && (
																	<div
																		className="w-3 h-3 rounded-full"
																		style={{
																			backgroundColor:
																				selectedColumn.color ||
																				'#e2e8f0',
																		}}
																	/>
																)}
																<span>
																	{selectedColumn?.name ||
																		'Select column...'}
																</span>
															</div>
															<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
														</>
													);
												})()}
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-full p-0">
											<Command>
												<CommandInput
													placeholder="Search columns..."
													className="h-9"
												/>
												<CommandList>
													<CommandEmpty>
														No column found.
													</CommandEmpty>
													<CommandGroup>
														{columns.map(
															(column) => (
																<CommandItem
																	key={
																		column._id
																	}
																	value={
																		column._id
																	}
																	onSelect={() => {
																		setSelectedColumnId(
																			column._id
																		);
																		setColumnOpen(
																			false
																		);
																	}}
																>
																	<div className="flex items-center gap-2">
																		<div
																			className="w-3 h-3 rounded-full"
																			style={{
																				backgroundColor:
																					column.color,
																			}}
																		/>
																		{
																			column.name
																		}
																	</div>
																	<Check
																		className={cn(
																			'ml-auto h-4 w-4',
																			selectedColumnId ===
																				column._id
																				? 'opacity-100'
																				: 'opacity-0'
																		)}
																	/>
																</CommandItem>
															)
														)}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
								</div>
							)}
						</>
					)}

					<div className="space-y-2">
						<Label htmlFor="name">Name</Label>
						<Input
							id="name"
							placeholder="Item name..."
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							autoFocus
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="description">Description</Label>
						<Textarea
							id="description"
							placeholder="optional"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
							className="resize-none"
						/>
					</div>

					<div className="flex justify-end gap-2 pt-4">
						<Button
							type="button"
							variant="outline"
							onClick={() => setOpen(false)}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={!name.trim() || isCreating}
						>
							{isCreating ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Creating...
								</>
							) : (
								'Create'
							)}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
};
